import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarSumarizacaoEcfApuracao,
  buscarLancamentosEcfApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { FILTER_FIELD_SX } from "../components/SelectFiltro";
import { formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const TIPO_OPCOES = [
  { value: "", label: "Todos" },
  { value: "A", label: "Adição" },
  { value: "E", label: "Exclusão" },
];

function exportarCsv(linhas, nomeArquivo) {
  const cabecalho = ["codigo", "descricao", "tipo_lancamento", "valor"].join(";");
  const corpo = linhas.map((l) =>
    [l.codigo, l.descricao, l.tipoLancamento, l.valor]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cabecalho, ...corpo].join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosEcf() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const anoInicial = searchParams.get("ano") || "";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  // TXM-599 — ano NÃO é um filtro interativo aqui: o card pede só "busca por código/descrição,
  // filtro por tipo" pra esta tela (o protótipo apuracao_icms_ecf_ajustes.html não tem seletor
  // de ano). O exercício é fixado pela linha clicada na aba (chega via ?ano= na URL); pra ver
  // outro ano, o usuário volta e clica em "Detalhes" na linha daquele ano.
  const ano = anoInicial;
  const [tipoLancamento, setTipoLancamento] = useState("");
  const [busca, setBusca] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [linhas, setLinhas] = useState([]);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracao() {
      const res = await buscarSumarizacaoEcfApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
    }
    carregarApuracao();
    return () => {
      ativo = false;
    };
  }, [id]);

  const carregarLancamentos = useCallback(async () => {
    if (!ano) {
      setLoading(false);
      setErro("Selecione um exercício.");
      return;
    }
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosEcfApuracao(id, {
      ano: Number(ano),
      tipoLancamento: tipoLancamento || undefined,
      busca: busca.trim() || undefined,
      pagina,
      pageSize,
    });
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
    setTotal(res.total);
  }, [id, ano, tipoLancamento, busca, pagina, pageSize]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  function limparFiltros() {
    setBusca("");
    setTipoLancamento("");
    setPagina(1);
  }

  // TXM-599 — esta tela usa paginação SERVER-SIDE (o card fala em "paginação", sem detalhar
  // o modo): `linhas` é só a página atual (LIMIT/OFFSET no backend), então exportar `linhas`
  // direto perderia o resto do filtro silenciosamente sempre que total > pageSize (ex.: 1.472
  // lançamentos filtrados, só 10 exportados). Busca todas as páginas (pageSize máximo do
  // backend, 100) respeitando o filtro atual antes de gerar o CSV.
  async function handleExportar() {
    setExportando(true);
    try {
      const nomeArquivo = `apuracao_icms_ecf_lancamentos_${ano}.csv`;
      let acumulado = [];
      let paginaBusca = 1;
      const PAGE_SIZE_EXPORT = 100;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await buscarLancamentosEcfApuracao(id, {
          ano: Number(ano),
          tipoLancamento: tipoLancamento || undefined,
          busca: busca.trim() || undefined,
          pagina: paginaBusca,
          pageSize: PAGE_SIZE_EXPORT,
        });
        if (!res.sucesso || !res.linhas?.length) break;
        acumulado = acumulado.concat(res.linhas);
        if (acumulado.length >= res.total || res.linhas.length < PAGE_SIZE_EXPORT) break;
        paginaBusca += 1;
      }
      exportarCsv(acumulado, nomeArquivo);
    } finally {
      setExportando(false);
    }
  }

  if (loading && linhas.length === 0 && !erro) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  return (
    // navigate(-1) em vez de reconstruir a URL: `id` aqui é o id da apuração ECF (filha),
    // não o id do pai ICMS que /detalhe/icms espera — mesmo padrão de
    // LancamentosEcd/LancamentosNotasFiscais, sem tela standalone "/detalhe/ecf" pra voltar.
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        { label: `Apuração ICMS — ${apuracao?.empresa || "—"}`, onClick: () => navigate(-1) },
        { label: "Lançamentos ECF" },
      ]}
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={DescriptionIcon}
        titulo="Lançamentos de Ajuste do Lucro Líquido (SPED ECF)"
        subtitulo={
          <>
            {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
            <br />
            Exercício: {ano || "—"}
          </>
        }
        acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {total} lançamentos no filtro
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={total === 0 || exportando}
                onClick={handleExportar}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                {exportando ? "Exportando…" : "Exportar CSV"}
              </Button>
            </Box>
          }
          filtros={
            // TXM-599 — só os 2 filtros que o card pede pra esta tela (busca por
            // código/descrição + tipo), mesma ordem do protótipo apuracao_icms_ecf_ajustes.html.
            <>
              <TextField
                size="small"
                label="Código ou descrição"
                placeholder="Pesquisar código ou descrição"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPagina(1);
                }}
                sx={{ ...FILTER_FIELD_SX, width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#3c4043" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <SelectFiltro
                label="Tipo"
                value={tipoLancamento}
                onChange={(v) => {
                  setTipoLancamento(v);
                  setPagina(1);
                }}
                options={TIPO_OPCOES}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={limparFiltros}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              >
                Limpar filtros
              </Button>
            </>
          }
          paginacao={
            !loading &&
            linhas.length > 0 && (
              <Pagination
                paginationKey="lancamentos-ecf"
                serverSideTotal={total}
                serverSidePage={pagina}
                serverSideRowsPerPage={pageSize}
                onServerPageChange={(novaPagina) => setPagina(novaPagina)}
                onServerRowsPerPageChange={(novoPageSize) => {
                  setPageSize(novoPageSize);
                  setPagina(1);
                }}
                disabled={loading}
              />
            )
          }
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <GridLoader color="#1565c0" size={10} />
            </Box>
          ) : linhas.length === 0 && !erro ? (
            <p className={styles.emptyMsg}>Nenhum lançamento encontrado com os filtros informados.</p>
          ) : (
              <div style={{ margin: "8px 0 0", overflow: "auto" }}>
                <table className={tableStyles.accordion_table}>
                  <thead>
                    <tr>
                      <th scope="col">Código</th>
                      <th scope="col">Descrição</th>
                      <th scope="col">Tipo</th>
                      <th scope="col" style={{ textAlign: "right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => (
                      <tr key={`${l.codigo}-${i}`}>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.codigo || "—"}</td>
                        <td>{l.descricao || "—"}</td>
                        <td>
                          <span
                            className={styles.badgeTipo}
                            style={
                              l.tipoLancamento === "A"
                                ? { background: "#fdecd8", color: "#ad4e00" }
                                : { background: "#e8f5e9", color: "#2e7d32" }
                            }
                          >
                            {l.tipoLancamento === "A" ? "Adição" : "Exclusão"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{formatarMoedaBRL(l.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
